// File: frontend/src/components/Header/Header.js

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import Button from '../Button/Button';
import styles from './Header.module.css';

const Header = ({ 
  isDashboard, 
  onMobileMenuToggle,
  isMobileMenuOpen,
  chatId,
  onNewChat,
  onShareChat,
  showHistoryButton
}) => {
  const { t } = useLanguage();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [logoFade, setLogoFade] = useState(1);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 0);
      setLogoFade(Math.max(0, 1 - scrollPosition / 200));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);

  const renderNavigation = () => (
    <nav className={`${styles.navigation} ${isMobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
      <Link to="/quote">{t('insurance')}</Link>
      <Link to="/ai-agents">{t('ai_agents')}</Link>
      <Link to="/capabilities">{t('capabilities_title')}</Link>
      <Link to="/news">{t('news_title')}</Link>
      {/* <Link to="/join">{t('join_title')}</Link> */}
      {/* <Link to="/contact">{t('contact_title')}</Link> */}
      <Link to="/sign-in">{t('sign_in')}</Link>
    </nav>
  );

  const Logo = ({ fade }) => (
    <svg width="200" height="53" viewBox="0 0 200 53" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.logo}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 34.4415L11.2851 2.94549C11.7867 1.53302 12.0374 0.710535 12.0374 0.477995C12.0374 0.245456 12.0288 0.103351 12.0115 0.0516752L12.0374 0C13.1616 0.0516754 14.355 0.0775128 15.6175 0.0775128C16.6553 0.0775128 17.9005 0.0602879 19.3533 0.0258376L19.3792 0.0775128C19.3447 0.146413 19.3274 0.24115 19.3274 0.361726C19.3274 0.689004 19.5868 1.55886 20.1056 2.97132L31.6242 34.519C31.5075 34.5132 27.8679 34.5025 23.0964 34.4908L15.6962 8.65487L8.50098 34.4586C3.66562 34.4486 0 34.4415 0 34.4415ZM8.50098 34.4586L7.0509 39.6589H24.5767L23.0964 34.4908C18.5694 34.4798 13.0235 34.4679 8.50098 34.4586Z"
        fill="currentColor"
      />
      <g style={{ opacity: fade }}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M41.2747 34.4421C41.2055 34.0454 41.1709 33.1909 41.1709 31.8786V0.337706H34.6852V34.4421H41.2747ZM54.4794 31.8786C54.4794 33.1909 54.514 34.0454 54.5832 34.4421H47.9938V0.337706H54.4794V31.8786ZM61.5105 15.5533C60.8187 17.2758 60.4728 19.1447 60.4728 21.16C60.4728 25.1563 61.9169 28.4376 64.8052 31.0041C67.6762 33.5535 71.3082 34.8281 75.7012 34.8281C76.4967 34.8281 77.3269 34.7764 78.1917 34.6731C81.1319 34.3286 83.2592 33.7085 84.5736 32.8128L84.859 28.7821C84.9109 28.0242 84.9541 27.2835 84.9887 26.5601C84.7292 26.784 84.3401 27.0252 83.8213 27.2835C81.8496 28.2654 79.4888 28.7563 76.7389 28.7563C74.11 28.7563 71.9741 28.2438 70.331 27.2189C68.688 26.194 67.6416 24.7601 67.1919 22.917H76.3238C81.6334 22.917 85.153 22.6758 86.8825 22.1935C87.09 21.694 87.1938 20.7639 87.1938 19.4031C87.1938 15.8547 86.0956 12.9092 83.8991 10.5666C81.668 8.13787 78.7105 6.92351 75.0267 6.92351C70.6683 6.92351 67.1401 8.29289 64.442 11.0317C63.1795 12.3236 62.2023 13.8308 61.5105 15.5533ZM80.293 17.9045C79.5839 14.2011 77.5604 12.3494 74.2224 12.3494C71.5935 12.3494 69.5959 13.4777 68.2296 15.7342C67.8145 16.4232 67.4946 17.1811 67.2697 18.0079C67.6848 18.0251 68.195 18.0423 68.8003 18.0595H70.7201C71.36 18.0768 71.974 18.0854 72.562 18.0854H74.0408C74.9228 18.0854 75.7876 18.0768 76.635 18.0595L78.7624 17.982C79.3331 17.9648 79.8433 17.939 80.293 17.9045ZM98.9977 29.2616C98.9977 28.7707 99.3177 28.1033 99.9576 27.2592C101.428 27.7588 103.019 28.0085 104.731 28.0085C108.242 28.0085 111.13 26.9492 113.396 24.8305C114.416 23.8487 115.203 22.7075 115.757 21.407C116.31 20.1065 116.587 18.6553 116.587 17.0534C116.587 15.4514 116.215 13.9356 115.471 12.506C117.391 12.1615 118.731 11.5758 119.492 10.749C119.406 10.1806 119.155 9.12986 118.74 7.59682L118.273 5.73651C118.066 4.97861 117.884 4.39296 117.728 3.97956L117.573 3.95372C117.279 4.281 116.604 4.80205 115.549 5.51689C114.494 6.23174 112.843 7.06284 110.594 8.01022C108.882 7.28677 107.049 6.92504 105.094 6.92504C101.376 6.92504 98.3492 8.01021 96.0143 10.1806C93.8005 12.2476 92.6936 14.8141 92.6936 17.8802C92.6936 20.619 93.6708 22.9099 95.6251 24.753C93.9475 26.0793 93.1087 27.6985 93.1087 29.6104C93.1087 30.7473 93.2427 31.7593 93.5108 32.6464C93.7789 33.5335 94.3107 34.3043 95.1063 34.9588C92.1834 36.5608 90.7738 39.1531 90.8776 42.7359C90.9468 45.8192 92.2958 48.291 94.9247 50.1513C97.5536 52.0116 101.013 52.9418 105.302 52.9418C109.556 52.9418 112.765 52.1193 114.927 50.4743C117.088 48.8293 118.169 46.4738 118.169 43.4077C118.169 40.3416 117.313 37.9732 115.601 36.3024C114.684 35.4239 113.487 34.6832 112.008 34.0803C110.529 33.4775 108.484 32.9736 105.873 32.5688C103.261 32.1641 101.384 31.6947 100.243 31.1607C99.7241 30.9195 99.3868 30.6655 99.2312 30.3985C99.0755 30.1315 98.9977 29.7526 98.9977 29.2616ZM99.2826 15.6309C99.0405 16.251 98.9194 16.8797 98.9194 17.517C98.9194 18.1543 99.0534 18.7658 99.3215 19.3515C99.5896 19.9371 99.983 20.4625 100.502 20.9276C101.661 21.9611 103.174 22.4778 105.042 22.4778C106.46 22.4778 107.688 22.0041 108.726 21.0567C109.815 20.0232 110.36 18.7787 110.36 17.3232C110.36 15.8677 109.867 14.6921 108.881 13.7964C107.896 12.9007 106.538 12.4528 104.808 12.4528C101.955 12.4528 100.113 13.5122 99.2826 15.6309ZM112.021 43.0193C112.021 45.9821 109.893 47.4634 105.639 47.4634C103.148 47.4634 101.116 46.9553 99.5422 45.939C98.8331 45.4739 98.2797 44.9313 97.8819 44.3112C97.4841 43.6911 97.2809 42.9548 97.2723 42.1021C97.2636 41.2495 97.5274 40.3495 98.0635 39.4021C98.5997 38.4547 99.4385 37.6796 100.58 37.0767L101.929 37.3092C105.717 37.8604 108.389 38.6269 109.945 39.6088C111.329 40.47 112.021 41.6069 112.021 43.0193ZM121.801 21.0825C121.801 19.1877 122.126 17.3748 122.774 15.6437C123.423 13.9126 124.352 12.4011 125.563 11.1092C128.209 8.31873 131.642 6.92351 135.862 6.92351C140.065 6.92351 143.403 8.24122 145.876 10.8767C148.246 13.3915 149.43 16.6556 149.43 20.6691C149.43 24.6826 148.211 28.007 145.772 30.6424C143.195 33.4329 139.78 34.8281 135.525 34.8281C131.132 34.8281 127.69 33.4587 125.2 30.7199C122.934 28.2223 121.801 25.0098 121.801 21.0825ZM128.65 24.08C128.287 23.0638 128.105 22.013 128.105 20.9279C128.105 18.7058 128.71 16.8197 129.921 15.2694C131.253 13.5814 133.095 12.7373 135.447 12.7373C137.764 12.7373 139.589 13.5297 140.921 15.1144C142.166 16.5958 142.789 18.5422 142.789 20.9537C142.789 23.3308 142.183 25.26 140.973 26.7413C139.727 28.2744 138.024 29.0409 135.862 29.0409C133.527 29.0409 131.642 28.2571 130.206 26.6896C129.532 25.9662 129.013 25.0963 128.65 24.08ZM160.949 31.5467C160.949 33.0281 160.983 33.9927 161.052 34.4405H154.593V7.44026H160.949V9.42976C162.419 7.75892 163.962 6.92351 165.579 6.92351C167.196 6.92351 168.472 7.1216 169.406 7.51778L168.939 14.4681L168.835 14.5456C168.23 13.4776 166.976 12.9437 165.073 12.9437C164.347 12.9437 163.608 13.1891 162.855 13.68C162.103 14.171 161.467 14.8212 160.949 15.6308V31.5467ZM175.588 42.0631L173.876 43.4325L180.569 45.7578C183.526 43.4152 187.608 36.1463 192.814 23.9509L196.705 14.3652C198.003 10.9891 199.101 8.68092 200 7.44071H192.529L186.743 23.0466L180.517 7.44071H173.253L183.189 29.9711L181.996 32.8391C180.301 36.9042 178.165 39.9788 175.588 42.0631Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );

  const handleShareClick = (type) => {
    onShareChat?.(type);
    setShowShareMenu(false);
    showHistoryButton(true);
  };

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest(`.${styles.shareContainer}`)) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showShareMenu]);

  const renderChatControls = () => {
    if (!isDashboard || !chatId) return null;
    
    return (
      <div className={styles.chatControls}>
        <button 
          className={styles.headerActionButton} 
          onClick={onNewChat}
          title="New Chat"
        >
          <img src="/icons/18/misc/plus.svg" alt="New Chat" />
        </button>
        <div className={styles.shareContainer}>
          <button 
            className={styles.headerActionButton} 
            onClick={() => setShowShareMenu(!showShareMenu)}
            title="Share Chat"
          >
            <img src="/icons/18/misc/share.svg" alt="Share" />
          </button>
          {showShareMenu && (
            <div className={styles.shareMenu}>
              <button 
                className={styles.shareMenuItem}
                onClick={() => handleShareClick('email')}
              >
                <img src="/icons/18/misc/email.svg" alt="Email" />
                Share via email
              </button>
              <button 
                className={styles.shareMenuItem}
                onClick={() => handleShareClick('download')}
              >
                <img src="/icons/18/misc/download.svg" alt="Download" />
                Download as JSON
              </button>
            </div>
          )}
        </div>
        {showHistoryButton && (
          <Link 
            to="/ai1/chat"
            className={styles.headerActionButton}
            title="Chat History"
          >
            <img src="/icons/18/misc/date.svg" alt="Chat History" />
          </Link>
        )}
      </div>
    );
  };

  const getBreadcrumbSteps = () => {
    const pathSegments = location.pathname
      .split('/')
      .filter(Boolean);
    
    if (pathSegments[0] === 'dashboard') {
      pathSegments.shift(); // Remove 'dashboard' as we'll add 'Allegory Dashboard'
    }
    
    return ['Allegory Dashboard', ...pathSegments];
  };

  const handleBreadcrumbClick = (index, step) => {
    if (index === 0) {
      navigate('/dashboard');
    } else {
      // Get the current path segments
      const pathSegments = location.pathname
        .split('/')
        .filter(Boolean);
      
      if (pathSegments[0] === 'dashboard') {
        pathSegments.shift();
      }
      
      // Create the new path using segments up to the clicked index
      const newPath = index === 1 
        ? `/${pathSegments[0]}` 
        : `/${pathSegments.slice(0, index).join('/')}`;
      
      navigate(newPath);
    }
  };
    
  if (isDashboard) {
    return (
      <header className={`${styles.dashboardHeader} ${isScrolled ? styles.scrolled : ''}`}>
        <div className={styles.headerLeft}>
          <button 
            className={`${styles.mobileMenuButton} ${isMobileMenuOpen ? styles.active : ''}`} 
            onClick={onMobileMenuToggle}
            type="button"
          >
            <img 
              src={isMobileMenuOpen ? "/icons/18/misc/close.svg" : "/icons/18/misc/menu.svg"} 
              alt={isMobileMenuOpen ? "Close Menu" : "Open Menu"} 
            />
          </button>
          <Breadcrumb 
            steps={getBreadcrumbSteps()}
            activeStep={getBreadcrumbSteps().length - 1}
            onStepClick={handleBreadcrumbClick}
          />
        </div>
        <div className={styles.headerRight}>
          {renderChatControls()}
        </div>
      </header>
    );
  }

  return (
    <header className={`${styles.landingHeader} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.headerContent}>
        <Link to="/" className={styles.logoLink}>
          <Logo fade={logoFade} />
        </Link>
        <button 
          className={styles.mobileMenuButton} 
          onClick={onMobileMenuToggle}
          type="button"
        >
          <img 
            src={isMobileMenuOpen ? "/icons/18/misc/close.svg" : "/icons/18/misc/menu.svg"} 
            alt={isMobileMenuOpen ? "Close Menu" : "Open Menu"} 
          />
        </button>
        {renderNavigation()}
        <div className={styles.rightSection}>
          <Button
            // href="/sign-up"
            href="/quote"
            variant="secondary"
            size="small"
            showArrow
            className={styles.createAccountButton}
          >
            {/* {t('get_started')} */}
            {t('hero.cta_button')}
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;